import type { Character } from '../data/types';
import { loadCharacters, spriteUrl } from '../data/api';
import { getCharStats, getStreak } from '../achievements';

const MAX_TEAM = 8;

function speakName(name: string) {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(name);
    u.rate = 0.95;
    u.pitch = 1.1;
    u.volume = 1;
    // Prefer an English voice if available
    const voices = synth.getVoices();
    const en = voices.find(v => /en[-_]/i.test(v.lang)) || voices[0];
    if (en) u.voice = en;
    synth.speak(u);
  } catch {}
}

export async function renderBuilder(
  root: HTMLElement,
  onFight: (left: Character[], right: Character[]) => void
) {
  root.innerHTML = `<div class="loading">Loading roster</div>`;
  let chars: Character[] = [];
  try { chars = await loadCharacters(); }
  catch (e) {
    root.innerHTML = `<div class="loading">Failed to load: ${(e as Error).message}</div>`;
    return;
  }

  const left: Character[] = [];
  const right: Character[] = [];
  let filter: 'all' | 'pokemon' | 'plant' | 'zombie' = 'all';
  let activeSide: 'left' | 'right' = 'left';

  root.innerHTML = `
    <div class="builder">
      <div class="builder-header">
        <div class="builder-title">⚔️ Build Your Teams ${getStreak() > 0 ? `<span class="streak-badge">🔥 ${getStreak()} streak</span>` : ''}</div>
        <div class="builder-filters">
          <button class="filter-chip active" data-f="all">All</button>
          <button class="filter-chip" data-f="pokemon">Pokémon</button>
          <button class="filter-chip" data-f="plant">Plants</button>
          <button class="filter-chip" data-f="zombie">Zombies</button>
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn-secondary" id="random-btn">🎲 Random</button>
          <button class="btn-secondary" id="clear-btn">Clear</button>
        </div>
      </div>

      <div class="team-panel left">
        <h2>← Team Left (<span id="left-count">0</span>/${MAX_TEAM})</h2>
        <div class="team-slots" id="left-slots"></div>
      </div>

      <div class="character-grid" id="char-grid"></div>

      <div class="team-panel right">
        <h2>Team Right → (<span id="right-count">0</span>/${MAX_TEAM})</h2>
        <div class="team-slots" id="right-slots"></div>
      </div>

      <div class="builder-footer">
        <div style="color: var(--muted); font-size: 14px;">
          Click a character to add to <b id="active-side-label" style="color:var(--left)">Team Left</b>.
          Click again on the panel to switch sides.
        </div>
        <button class="btn-primary" id="fight-btn" disabled>FIGHT! ⚔️</button>
      </div>
    </div>
    <div class="char-tooltip" id="tooltip" style="display:none"></div>
  `;

  const grid = root.querySelector('#char-grid') as HTMLElement;
  const leftSlots = root.querySelector('#left-slots') as HTMLElement;
  const rightSlots = root.querySelector('#right-slots') as HTMLElement;
  const leftCount = root.querySelector('#left-count') as HTMLElement;
  const rightCount = root.querySelector('#right-count') as HTMLElement;
  const fightBtn = root.querySelector('#fight-btn') as HTMLButtonElement;
  const tooltip = root.querySelector('#tooltip') as HTMLElement;
  const sideLabel = root.querySelector('#active-side-label') as HTMLElement;

  function renderSlots() {
    const draw = (arr: Character[], el: HTMLElement, side: 'left'|'right') => {
      el.innerHTML = '';
      for (let i = 0; i < MAX_TEAM; i++) {
        const c = arr[i];
        const slot = document.createElement('div');
        slot.className = 'team-slot' + (c ? ' filled' : '');
        if (c) {
          slot.innerHTML = `<img src="${spriteUrl(c)}" alt=""><button class="remove">×</button>`;
          slot.querySelector('.remove')!.addEventListener('click', (e) => {
            e.stopPropagation();
            arr.splice(i, 1);
            update();
          });
        } else {
          slot.textContent = '+';
        }
        el.appendChild(slot);
      }
    };
    draw(left, leftSlots, 'left');
    draw(right, rightSlots, 'right');
    leftCount.textContent = String(left.length);
    rightCount.textContent = String(right.length);
    fightBtn.disabled = left.length === 0 || right.length === 0;
  }

  function renderGrid() {
    grid.innerHTML = '';
    for (const c of chars) {
      if (filter !== 'all' && c.faction !== filter) continue;
      const card = document.createElement('div');
      card.className = `char-card faction-${c.faction}`;
      card.innerHTML = `
        <img src="${spriteUrl(c)}" alt="${c.name}" loading="lazy">
        <div class="name">${c.name}</div>
        <div class="stats">
          <span>❤️${c.hp}</span><span>⚔️${c.attack}</span><span>💨${c.speed}</span>
        </div>
      `;
      card.addEventListener('click', () => {
        const arr = activeSide === 'left' ? left : right;
        if (arr.length < MAX_TEAM) {
          arr.push(c);
          speakName(c.name);
          update();
        }
      });
      card.addEventListener('mouseenter', (e) => {
        tooltip.style.display = 'block';
        const cs = getCharStats(c.id);
        tooltip.innerHTML = `
          <h4>${c.name}</h4>
          <div class="stat-row"><span>HP</span><b>${c.hp}</b></div>
          <div class="stat-row"><span>Attack</span><b>${c.attack}</b></div>
          <div class="stat-row"><span>Defense</span><b>${c.defense}</b></div>
          <div class="stat-row"><span>Speed</span><b>${c.speed}</b></div>
          <div class="stat-row"><span>Type</span><b>${c.range_type}</b></div>
          <div class="stat-row"><span>Ability</span><b>${c.ability_id}</b></div>
          <div class="desc">${c.description}</div>
          <div class="lifetime-stats">
            <span>🏆 ${cs.wins}</span><span>⚔️ ${cs.kills}</span><span>💥 ${Math.round(cs.dmg)}</span>
          </div>
        `;
      });
      card.addEventListener('mousemove', (e) => {
        tooltip.style.left = (e.clientX + 16) + 'px';
        tooltip.style.top = (e.clientY + 16) + 'px';
      });
      card.addEventListener('mouseleave', () => { tooltip.style.display = 'none'; });
      grid.appendChild(card);
    }
  }

  function update() {
    renderSlots();
    renderGrid();
    sideLabel.textContent = activeSide === 'left' ? 'Team Left' : 'Team Right';
    sideLabel.style.color = activeSide === 'left' ? 'var(--left)' : 'var(--right)';
  }

  root.querySelectorAll('.filter-chip').forEach(btn => {
    btn.addEventListener('click', () => {
      root.querySelectorAll('.filter-chip').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      filter = (btn as HTMLElement).dataset.f as any;
      renderGrid();
    });
  });
  root.querySelector('.team-panel.left')!.addEventListener('click', () => { activeSide = 'left'; update(); });
  root.querySelector('.team-panel.right')!.addEventListener('click', () => { activeSide = 'right'; update(); });
  root.querySelector('#random-btn')!.addEventListener('click', () => {
    left.length = 0; right.length = 0;
    const shuf = [...chars].sort(() => Math.random() - 0.5);
    for (let i = 0; i < 6; i++) left.push(shuf[i]);
    for (let i = 6; i < 12; i++) right.push(shuf[i]);
    update();
  });
  root.querySelector('#clear-btn')!.addEventListener('click', () => {
    left.length = 0; right.length = 0; update();
  });
  fightBtn.addEventListener('click', () => onFight(left, right));

  update();
}
